import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, LayoutGrid, Image as ImageIcon, Loader2, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import DesignQualityPanel from './DesignQualityPanel';
import { ActionableDesignSuggestions } from './ActionableDesignSuggestions';
import { toast } from 'sonner';
import { simplifyRecommendation, simplifySectionHeader } from '../utils/languageSimplifier';
import { withBackend } from '../utils/api';

export function SlideBySlideTab({ slides, analysis, expertiseLevel = 'beginner', analysisFocus = 'both' }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showMap, setShowMap] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [generatingNanoBanana, setGeneratingNanoBanana] = useState(false);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [localSlides, setLocalSlides] = useState(slides);
  const [selectedNanoBananaModel, setSelectedNanoBananaModel] = useState('flash'); // 'flash' or 'pro'

  // Navigation functions
  const goToNext = () => {
    if (!localSlides || localSlides.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % localSlides.length);
  };

  const goToPrevious = () => {
    if (!localSlides || localSlides.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + localSlides.length) % localSlides.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setShowMap(false);
  };

  const handleGeneratePreview = async () => {
    setGeneratingPreview(true);
    try {
      toast.info('Generating preview...');

      // Get file_hash from analysis data, slides, or extract from lazy_thumbnail_url
      let fileHash = analysis?.file_hash || slides?.[0]?.file_hash;

      if (!fileHash && slides?.[0]?.lazy_thumbnail_url) {
        // Extract hash from lazy_thumbnail_url format: /thumbnail/{hash}/{slide_number}
        const match = slides[0].lazy_thumbnail_url.match(/\/thumbnail\/([^/]+)\/\d+/);
        if (match) {
          fileHash = match[1];
          console.log('✅ Extracted file hash from lazy_thumbnail_url:', fileHash);
        }
      }

      if (!fileHash) {
        console.error('File hash not found in analysis:', analysis);
        console.error('File hash not found in slides:', slides?.[0]);
        throw new Error('File hash not available');
      }

      // Call backend on-demand thumbnail endpoint
      const response = await fetch(withBackend(`/thumbnail/${fileHash}/${slideNumber}`));
      if (!response.ok) {
        throw new Error(`Thumbnail generation failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.thumbnail) {
        // Update the current slide's thumbnail_url
        currentSlide.thumbnail_url = data.thumbnail;
        // Clear the image load error for this slide to show the new thumbnail
        setImageLoadErrors(prev => ({ ...prev, [slideNumber]: false }));
        toast.success('Preview generated successfully!');
      } else {
        throw new Error('No thumbnail data received');
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleGenerateNanoBanana = async () => {
    setGeneratingNanoBanana(true);
    try {
      toast.info('🍌 Generating Nano Banana Pro preview...');

      const currentSlide = localSlides[currentIndex];
      const brandContext = analysis?.company_context || '';
      const fileHash = analysis?.file_hash;
      const slideNumber = currentSlide.slide_number || currentIndex + 1;

      // Get report_hash from URL if viewing shared report
      const urlParams = new URLSearchParams(window.location.search);
      const reportHash = urlParams.get('report');

      const response = await fetch(withBackend('/generate-nano-preview'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slide: currentSlide,
          brand_context: brandContext,
          model: selectedNanoBananaModel,
          file_hash: fileHash,
          slide_number: slideNumber,
          report_hash: reportHash, // Include report hash for Supabase persistence
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate Nano Banana preview: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success && data.nano_banana_preview_url) {
        // Update local slides state with the new preview URL
        setLocalSlides(prevSlides => {
          const updatedSlides = [...prevSlides];
          updatedSlides[currentIndex] = {
            ...updatedSlides[currentIndex],
            nano_banana_preview_url: data.nano_banana_preview_url,
          };
          return updatedSlides;
        });
        toast.success('🍌 Nano Banana Pro preview generated!');
      } else {
        throw new Error(data.error || 'No preview data received');
      }
    } catch (error) {
      console.error('Failed to generate Nano Banana preview:', error);
      toast.error('Failed to generate Nano Banana preview');
    } finally {
      setGeneratingNanoBanana(false);
    }
  };

  // Sync localSlides with slides prop
  useEffect(() => {
    setLocalSlides(slides);
  }, [slides]);

  // Keyboard navigation - must be called before any early returns
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [localSlides]);

  // Early return AFTER all hooks
  if (!localSlides || localSlides.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Slide-by-Slide Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No slides available for analysis.</p>
        </CardContent>
      </Card>
    );
  }

  const currentSlide = localSlides[currentIndex];
  const slideNumber = currentSlide.slide_number || currentIndex + 1;
  const totalSlides = localSlides.length;

  // Extract slide data
  const recommendations = currentSlide.recommendations || {};
  const newTitle = recommendations.newTitle || '';
  const contentRefinements = recommendations.content_refinements || [];
  const visualContent = recommendations.visual_content || [];
  const designQuality = currentSlide.design_quality;

  return (
    <div className="space-y-6">
      {/* Header with slide counter and map toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Slide-by-Slide Analysis</h2>
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="bg-accent/10 text-accent">
            Slide {slideNumber} of {totalSlides}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMap(!showMap)}
            className="flex items-center gap-2"
          >
            <LayoutGrid className="w-4 h-4" />
            {showMap ? 'Hide' : 'Show'} Map
          </Button>
        </div>
      </div>

      {/* Slide Navigator Map (collapsible) */}
      {showMap && (
        <Card className="border-border/40 bg-card/60">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="text-sm font-medium text-muted-foreground text-center">
                Jump to slide:
              </div>
              <div className="grid grid-cols-10 gap-2">
                {slides.map((slide, index) => {
                  const isActive = index === currentIndex;
                  const slideNum = slide.slide_number || index + 1;
                  const hasIssues = (slide.recommendations?.content_refinements?.length || 0) + (slide.recommendations?.visual_content?.length || 0) > 0;

                  const thumbnailUrl = slide.thumbnail_url ||
                    (slide.lazy_thumbnail_url ? withBackend(slide.lazy_thumbnail_url) : null);

                  return (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`
                        relative h-20 rounded-lg border-2 transition-all overflow-hidden
                        ${isActive
                          ? 'border-accent bg-accent/10 shadow-md scale-105'
                          : 'border-border bg-card hover:border-accent/50 hover:bg-accent/5'
                        }
                        flex flex-col items-center justify-center text-sm font-semibold
                      `}
                      title={`Slide ${slideNum}${hasIssues ? ' - Has recommendations' : ''}`}
                    >
                      {thumbnailUrl ? (
                        <div className="absolute inset-0">
                          <img
                            src={thumbnailUrl}
                            alt={`Slide ${slideNum}`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        </div>
                      ) : null}
                      <span className={`relative z-10 drop-shadow-md ${isActive ? 'text-white font-bold' : 'text-white'}`}>
                        {slideNum}
                      </span>
                      {hasIssues && (
                        <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-orange-500 shadow-lg"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Carousel Container */}
      <Card className="border-border/40 bg-card/60">
        <CardContent className="p-6 space-y-6">
          {/* Full-Width Slide Preview with Navigation */}
          <div className="relative">
            {/* Navigation Arrows */}
            <Button
              variant="outline"
              size="icon"
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-lg hover:bg-accent bg-background/95 backdrop-blur"
              disabled={totalSlides <= 1}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-lg hover:bg-accent bg-background/95 backdrop-blur"
              disabled={totalSlides <= 1}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>

            {/* Large Thumbnail View - Centered with Max Width */}
            <div className="w-full flex justify-center">
              {currentSlide.nano_banana_preview_url ? (
                <div className="w-full max-w-6xl grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-muted-foreground text-center uppercase tracking-wide">Original Slide</p>
                    <div className="aspect-video bg-muted rounded-lg overflow-hidden border-2 border-border shadow-xl">
                      {currentSlide.thumbnail_url && currentSlide.thumbnail_url.trim() !== '' && !imageLoadErrors[slideNumber] ? (
                        <img
                          src={currentSlide.thumbnail_url}
                          alt={`Slide ${slideNumber}`}
                          className="w-full h-full object-contain bg-white"
                          onError={() => {
                            setImageLoadErrors(prev => ({ ...prev, [slideNumber]: true }));
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-muted/50">
                          <div className="text-center">
                            <div className="text-4xl font-bold mb-2 text-muted-foreground/50">{slideNumber}</div>
                            <div className="text-sm text-muted-foreground">Preview not available</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-primary text-center uppercase tracking-wide">🍌 Nano Banana Pro Premium</p>
                    <div className="aspect-video bg-muted rounded-lg overflow-hidden border-2 border-primary shadow-xl relative">
                      <img
                        src={currentSlide.nano_banana_preview_url}
                        alt="AI Enhanced Preview"
                        className="w-full h-full object-contain bg-white"
                      />
                      <div className="absolute top-3 right-3 bg-primary/80 text-primary-foreground p-2 rounded-full">
                        <Sparkles className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full max-w-3xl aspect-video bg-muted rounded-lg overflow-hidden border-2 border-border shadow-xl relative">
                  {currentSlide.thumbnail_url && currentSlide.thumbnail_url.trim() !== '' && !imageLoadErrors[slideNumber] ? (
                    <img
                      src={currentSlide.thumbnail_url}
                      alt={`Slide ${slideNumber}`}
                      className="w-full h-full object-contain bg-white"
                      onError={() => {
                        setImageLoadErrors(prev => ({ ...prev, [slideNumber]: true }));
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground bg-muted/50 gap-4">
                      <div className="text-center">
                        <div className="text-6xl font-bold mb-4 text-muted-foreground/50">
                          {slideNumber}
                        </div>
                        <div className="text-lg text-muted-foreground mb-4">Slide preview not available</div>
                      </div>
                      <Button
                        onClick={handleGeneratePreview}
                        disabled={generatingPreview}
                        className="bg-accent hover:bg-accent/90"
                      >
                        {generatingPreview ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <ImageIcon className="w-4 h-4 mr-2" />
                            Generate Preview
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center max-w-md px-4">
                        Click to generate a thumbnail preview for this slide. This may take a few moments.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Progress Dots */}
            <div className="flex items-center justify-center gap-2 mt-4">
              {localSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`
                    h-2 rounded-full transition-all
                    ${index === currentIndex
                      ? 'bg-accent w-8'
                      : 'bg-border w-2 hover:bg-accent/50'
                    }
                  `}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Generate/Regenerate Nano Banana Button with Model Selection */}
            <div className="flex flex-col items-center gap-3 mt-4">
              <div className="flex items-center gap-4 text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="slideNanoBananaModel"
                    value="flash"
                    checked={selectedNanoBananaModel === 'flash'}
                    onChange={(e) => setSelectedNanoBananaModel(e.target.value)}
                    className="h-3 w-3 text-accent focus:ring-accent focus:ring-offset-0"
                  />
                  <span className="text-foreground">Gemini 2.5 Flash (Fast)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="slideNanoBananaModel"
                    value="pro"
                    checked={selectedNanoBananaModel === 'pro'}
                    onChange={(e) => setSelectedNanoBananaModel(e.target.value)}
                    className="h-3 w-3 text-accent focus:ring-accent focus:ring-offset-0"
                  />
                  <span className="text-foreground">Gemini 3 Pro ⭐ (Premium)</span>
                </label>
              </div>
              <Button
                onClick={handleGenerateNanoBanana}
                disabled={generatingNanoBanana}
                className="bg-primary hover:bg-primary/90"
              >
                {generatingNanoBanana ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Nano Banana Pro...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {currentSlide.nano_banana_preview_url ? 'Regenerate' : 'Generate'} Nano Banana Pro Preview
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Slide Analysis Below Preview */}
          <div className="border-t border-border pt-6 space-y-6">
            {/* Content Section - Combined Title + Refinements */}
            {((newTitle || contentRefinements.length > 0) && (analysisFocus === 'content' || analysisFocus === 'both')) && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b-2 border-blue-600/30">
                  <div className="text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                    {simplifySectionHeader('Content Improvements', expertiseLevel)}
                  </div>
                </div>

                {/* Content items with consistent styling */}
                <ul className="space-y-3">
                  {/* Suggested Title as first item if available */}
                  {newTitle && (
                    <li className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold mt-0.5">
                        ✏️
                      </span>
                      <div className="flex-1 space-y-1">
                        <div className="text-xs font-semibold text-blue-700 dark:text-blue-300">Suggested Title</div>
                        <div className="text-base font-bold text-foreground">{newTitle}</div>
                      </div>
                    </li>
                  )}

                  {/* Content Refinement items */}
                  {contentRefinements.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold mt-0.5">
                        {newTitle ? idx + 1 : idx + 1}
                      </span>
                      <span className="text-sm text-foreground leading-relaxed">{simplifyRecommendation(item, expertiseLevel)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Visual Design Quality - Combined with Visual Content Suggestions - Only show if analyzing design */}
            {(designQuality || visualContent.length > 0) && (analysisFocus === 'design' || analysisFocus === 'both') && (
              <div className="space-y-4">
                <details open className="group">
                  <summary className="flex items-center justify-between cursor-pointer pb-2 border-b-2 border-purple-600/30 list-none">
                    <div className="text-sm font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                      {simplifySectionHeader('Visual Design', expertiseLevel)}
                    </div>
                    <ChevronRight className="w-4 h-4 text-purple-600 dark:text-purple-400 group-open:rotate-90 transition-transform" />
                  </summary>

                  <div className="space-y-4 mt-4">
                    {/* Design Quality Panel Header and Content */}
                    {designQuality && (
                      <DesignQualityPanel
                        designQuality={designQuality}
                        slideNumber={slideNumber}
                        expertiseLevel={expertiseLevel}
                      />
                    )}

                    {/* Actionable Design Suggestions - NEW: More specific, contextual suggestions */}
                    {designQuality && (
                      <ActionableDesignSuggestions
                        slide={currentSlide}
                        designQuality={designQuality}
                        expertiseLevel={expertiseLevel}
                      />
                    )}

                    {/* Visual Content Suggestions - Integrated into Design Section */}
                    {visualContent.length > 0 && (
                      <div className="space-y-3 mt-6">
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {simplifySectionHeader('Visual Enhancement Suggestions', expertiseLevel)}
                        </div>
                        <ul className="space-y-2">
                          {visualContent.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center font-bold mt-0.5">
                                {idx + 1}
                              </span>
                              <span className="text-sm text-foreground">{simplifyRecommendation(item, expertiseLevel)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            {/* No recommendations or positive feedback when slide is good */}
            {(() => {
              const hasRecommendations = newTitle || contentRefinements.length > 0 || visualContent.length > 0;
              const designScore = designQuality?.scores?.overall || 0;
              const hasGoodDesign = designScore >= 80;

              if (!hasRecommendations && !designQuality) {
                // No data to show at all
                return (
                  <div className="text-center py-8 px-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="text-4xl mb-3">✨</div>
                    <p className="font-semibold text-green-700 dark:text-green-400">This slide looks great!</p>
                    <p className="text-sm text-green-600 dark:text-green-500 mt-1">No recommendations at this time.</p>
                  </div>
                );
              } else if (!hasRecommendations && hasGoodDesign) {
                // Has good design score, no recommendations
                return (
                  <div className="text-center py-8 px-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="text-4xl mb-3">🎯</div>
                    <p className="font-semibold text-green-700 dark:text-green-400">Excellent work on this slide!</p>
                    <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                      Design score: {designScore}/100 - Your slide has strong visual design with no major issues.
                    </p>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Keyboard Shortcuts Hint */}
      <div className="text-xs text-muted-foreground text-center">
        💡 Use <kbd className="px-2 py-1 bg-muted rounded border border-border">←</kbd> and{' '}
        <kbd className="px-2 py-1 bg-muted rounded border border-border">→</kbd> arrow keys to navigate
      </div>
    </div>
  );
}
